package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.observability.AnalyticsEvent
import com.evoapps.evoshift.observability.AppTelemetry

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.billing.BillingException
import com.evoapps.evoshift.domain.billing.BillingStore
import com.evoapps.evoshift.domain.billing.OwnerPlan
import com.evoapps.evoshift.domain.billing.PlanChange
import com.evoapps.evoshift.domain.billing.PlanOffer
import com.evoapps.evoshift.domain.billing.Plans
import com.evoapps.evoshift.domain.usecase.billing.GetPlanOffersUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObserveOwnerPlanUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObserveSeatsInUseUseCase
import com.evoapps.evoshift.domain.usecase.billing.PurchasePlanUseCase
import com.evoapps.evoshift.domain.usecase.billing.RestorePurchasesUseCase
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.paywall_change_scheduled
import evoshift.composeapp.generated.resources.paywall_restore_nothing
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class PlanChoiceState(
    /** Offers are loading and there are none to show yet. */
    val isLoadingOffers: Boolean = true,
    /** False in builds without a RevenueCat key: the paywall explains instead of selling. */
    val isBillingConfigured: Boolean = true,
    /** The seat tiers of the paywall's offering with store prices, by seats then Plans.OFFERED_PERIODS. */
    val offers: List<PlanOffer> = emptyList(),
    val selectedPackageId: String? = null,
    /** The owner's current plan; null until known. */
    val plan: OwnerPlan? = null,
    /** People in the owner's team across all workplaces; null before the first workplace or until known. */
    val seatsInUse: Int? = null,
    val isPurchasing: Boolean = false,
    val isRestoring: Boolean = false,
    /** A purchase (or restore) gave the owner a plan: the caller moves on. */
    val purchased: Boolean = false,
    val offersError: UiText? = null,
    val message: UiText? = null,
    val error: UiText? = null,
    /** SH-220: the person opened "Other plans" (the tiers and periods besides the recommended one). */
    val showOtherPlans: Boolean = false
) {
    val selectedOffer: PlanOffer? get() = offers.firstOrNull { it.packageId == selectedPackageId }
    val isBusy: Boolean get() = isPurchasing || isRestoring

    /** The seat tiers on sale, smallest first (from RevenueCat; nothing is hard-coded). */
    val seatTiers: List<Int> get() = offers.map { it.seats }.distinct().sorted()

    /** The periods of the selected tier. */
    val offersOfSelectedTier: List<PlanOffer> get() = selectedOffer?.let { selected -> offers.filter { it.seats == selected.seats } }.orEmpty()

    /** The product the owner pays for in a store, if any (marked "Current"). */
    val currentProductId: String? get() = plan?.storePlan?.entitlement?.productId?.takeIf { plan.isPaid }

    /** The seats of the owner's plan (a bought or granted one); null without one. */
    val currentSeats: Int? get() = plan?.seats?.takeIf { plan.isPaid }

    /** What buying the selected offer does to the running plan; null without a selection. */
    val change: PlanChange? get() = selectedOffer?.let { offer ->
        PlanChange.of(offer, plan?.storePlan?.takeIf { plan.isPaid }, plan?.billingStore ?: BillingStore.OTHER)
    }

    /** The plan was granted (promotional) and nothing is bought in a store: buying adds a subscription. */
    val isOnGrantedPlan: Boolean get() = plan?.isPaid == true && plan.storePlan == null

    /**
     * Whether a tier can be chosen for the team as it is today: it holds the team, or it is the
     * biggest tier on sale (a team bigger than every tier is never left without a plan, SH-220).
     */
    fun fitsTeam(seats: Int): Boolean = seatsInUse == null || seats >= seatsInUse || seats == seatTiers.lastOrNull()

    /**
     * SH-220: the one plan the paywall proposes as its primary button: the tier that holds
     * today's team (the owner not counted), or with a plan the next tier once the team outgrew
     * it; null while the plan fits, or when no tier holds the team ([isTeamBeyondTiers]).
     */
    val recommendedOffer: PlanOffer? get() = Plans.recommendedOffer(offers, seatsInUse ?: 0, currentSeats, currentProductId)

    /** SH-220: the team has more people than the plan's seats (it was never blocked). */
    val isOverPlan: Boolean get() = currentSeats?.let { seats -> seatsInUse?.let { it > seats } } == true

    /** SH-220: no tier on sale holds the team: "Larger team? Write to us" leads. */
    val isTeamBeyondTiers: Boolean get() = seatsInUse != null && seatTiers.isNotEmpty() && seatsInUse > seatTiers.last()

    /** SH-220: the biggest tier on sale, named by "Larger team? Write to us"; null without offers. */
    val biggestTier: Int? get() = seatTiers.lastOrNull()

    /** SH-220: the tiers and periods are listed: opened, or nothing to recommend. */
    val otherPlansShown: Boolean get() = showOtherPlans || recommendedOffer == null

    /** The selection is the recommended plan (it carries the "Fits your team" label). */
    val isRecommendedSelected: Boolean get() = recommendedOffer?.packageId?.let { it == selectedPackageId } == true

    val canSubscribe: Boolean get() = selectedOffer?.let { fitsTeam(it.seats) && change != PlanChange.CURRENT } == true
}

/**
 * The plan choice (SH-38, SH-52): the paywall screen and the plan step of onboarding, as
 * EVoLabels' PlanChoiceViewModel (KAN-111). Offers the seat tiers of the RevenueCat offering with
 * store prices, preselects the smallest tier that holds the team, buys the selected package
 * through the platform's billing and restores earlier purchases. Owners only: nothing here is
 * shown to employees.
 */
class PlanChoiceViewModel(
    private val getPlanOffers: GetPlanOffersUseCase,
    private val purchasePlan: PurchasePlanUseCase,
    private val restorePurchases: RestorePurchasesUseCase,
    private val observeOwnerPlan: ObserveOwnerPlanUseCase,
    private val observeSeatsInUse: ObserveSeatsInUseUseCase? = null
) : ViewModel() {
    private val _state = MutableStateFlow(PlanChoiceState())
    val state: StateFlow<PlanChoiceState> = _state.asStateFlow()

    /** The person picked a tier or period: the preselection no longer moves. */
    private var personChose = false

    init {
        AppTelemetry.log(AnalyticsEvent.PaywallViewed)
        viewModelScope.launch {
            var offersRequested = false
            // SH-188: a failed listener leaves the screen with a message, not an ended coroutine.
            observeOwnerPlan().catch { e -> _state.update { it.copy(error = e.toUiText()) } }.collect { plan ->
                _state.update { it.copy(plan = plan, isBillingConfigured = plan.isBillingConfigured).preselected() }
                // The plan arrives once the store SDK is logged in as the owner; only then ask
                // it for offers (iOS and the web cannot before).
                if (!offersRequested) {
                    offersRequested = true
                    loadOffers()
                }
            }
        }
        observeSeatsInUse?.let { seats ->
            viewModelScope.launch {
                seats().catch { e -> _state.update { it.copy(error = e.toUiText()) } }
                    .collect { used -> _state.update { it.copy(seatsInUse = used).preselected() } }
            }
        }
    }

    /**
     * Keeps the person's choice; otherwise the recommended plan (SH-220, [PlanChoiceState.recommendedOffer]),
     * else the smallest tier that holds the team (the largest if none does) in the preferred
     * period, never the plan the owner already has.
     */
    private fun PlanChoiceState.preselected(): PlanChoiceState {
        val kept = selectedPackageId?.takeIf { id -> offers.any { it.packageId == id } }
        if (personChose && kept != null) return this
        recommendedOffer?.let { return copy(selectedPackageId = it.packageId) }
        val candidates = offers.filter { it.productId != currentProductId }
        val pick = candidates.firstOrNull { fitsTeam(it.seats) } ?: candidates.lastOrNull()?.let { last ->
            candidates.first { it.seats == last.seats }
        }
        return copy(selectedPackageId = pick?.packageId ?: kept)
    }

    fun loadOffers() {
        _state.update { it.copy(offersError = null, isLoadingOffers = it.offers.isEmpty()) }
        viewModelScope.launch {
            getPlanOffers().fold(
                onSuccess = { offers ->
                    _state.update { it.copy(isLoadingOffers = false, offers = offers).preselected() }
                },
                onFailure = { e ->
                    val notConfigured = (e as? BillingException)?.reason == BillingException.Reason.NOT_CONFIGURED
                    _state.update {
                        it.copy(
                            isLoadingOffers = false,
                            isBillingConfigured = it.isBillingConfigured && !notConfigured,
                            offersError = if (notConfigured) null else e.toUiText()
                        )
                    }
                }
            )
        }
    }

    fun select(packageId: String) {
        personChose = true
        _state.update { it.copy(selectedPackageId = packageId, error = null) }
    }

    /** Picks a seat tier, keeping the period chosen so far when the tier has it. */
    fun selectSeats(seats: Int) {
        val state = _state.value
        val tier = state.offers.filter { it.seats == seats }
        val offer = tier.firstOrNull { it.period == state.selectedOffer?.period } ?: tier.firstOrNull() ?: return
        select(offer.packageId)
    }

    /** SH-220: opens or closes "Other plans"; closing it goes back to the recommended plan. */
    fun toggleOtherPlans() {
        val open = !_state.value.showOtherPlans
        if (!open) personChose = false
        _state.update { it.copy(showOtherPlans = open).let { s -> if (open) s else s.preselected() } }
    }

    fun subscribe() {
        val state = _state.value
        val offer = state.selectedOffer ?: return
        if (state.isBusy || !state.canSubscribe) return
        val change = state.change
        _state.update { it.copy(isPurchasing = true, error = null, message = null) }
        viewModelScope.launch {
            val error = purchasePlan(offer).exceptionOrNull()
            // A switch at the renewal changes nothing yet: stay and say so instead of closing.
            val scheduled = error == null && change == PlanChange.AT_RENEWAL
            // The plan's product id only: no price, no store or customer ids.
            if (error == null) AppTelemetry.log(AnalyticsEvent.Purchase(offer.productId))
            val reason = (error as? BillingException)?.reason
            _state.update {
                it.copy(
                    isPurchasing = false,
                    purchased = error == null && !scheduled,
                    // Closing the store sheet is not an error; being sent to the store's own
                    // page to change the plan is a pointer, not a failure.
                    message = when {
                        scheduled -> UiText.Resource(Res.string.paywall_change_scheduled)
                        reason == BillingException.Reason.CHANGE_IN_PORTAL -> error.toUiText()
                        else -> null
                    },
                    error = if (error != null && reason != BillingException.Reason.CANCELLED &&
                        reason != BillingException.Reason.CHANGE_IN_PORTAL
                    ) error.toUiText() else null
                )
            }
        }
    }

    fun restore() {
        if (_state.value.isBusy) return
        _state.update { it.copy(isRestoring = true, error = null, message = null) }
        viewModelScope.launch {
            restorePurchases().fold(
                onSuccess = { found ->
                    val isPaid = found || _state.value.plan?.isPaid == true
                    _state.update {
                        it.copy(
                            isRestoring = false,
                            purchased = isPaid,
                            message = if (isPaid) null else UiText.Resource(Res.string.paywall_restore_nothing)
                        )
                    }
                },
                onFailure = { e -> _state.update { it.copy(isRestoring = false, error = e.toUiText()) } }
            )
        }
    }

    fun messageShown() = _state.update { it.copy(message = null, error = null) }
}
